import React, {useEffect, useState} from 'react';
import {Table, Form, Tag, Skeleton, Alert, Col, Row} from 'antd';
import {
  attendingDinnerLabel,
  filterFoodOptionColumnsForSponsorAttendee,
  formatCurrencyByLocalChapter,
  formValueChangeHandler,
  getRequiredAttendeeFieldErrors,
  meetingPresentationLabel,
  occupiesRepSlot,
  scrollAndFocusFormErrorField,
  uniqueSponsorPerksValueEnum,
} from '../../../Util/commonUtility';
import {DollarOutlined} from '@ant-design/icons';
import {useParams} from 'react-router-dom';
import './style.css';
import AdminActionsDropdown from '../AdminActionsDropdown/AdminActionsDropdown';
import SectionLayout from '../Layout/sectionLayout';
import AddAttendeeeComponent from '../AttendeeAddComponent';
import SponsorEditableCell from '../SponsorEditableCell';
import useLocalization from '../../../Localization';

const CommonTable = ({
  allEventOptionsFoodData,
  sponsorOption,
  onSave,
  onEdit,
  sponsorPerksData,
  attendeesDataWithPerks,
  onDelete,
  eventType,
  alertInformation,
  setListAttendeesDataWithPerks,
  setDisablePaymentClicks,
  eventLocalChapterData,
  eventISOnline,
  inPersonRemaining,
  eventId,
  flush = false,
  allowAddRow = true,
  limitAttendees,
}) => {
  const {id} = useParams();
  const resolvedEventId = eventId || id;
  const {ApplicationString} = useLocalization();
  const [form] = Form.useForm();
  const AttendeeDinnerLimit =
    sponsorPerksData?.eventSponsorshipPerk?.dinner_with_speaker;
  const AttendeeDinnerPrice =
    sponsorPerksData?.eventSponsorshipPerk?.dinner_with_speaker_price;
  const AttendeePresentationLimit =
    sponsorPerksData?.eventSponsorshipPerk?.workshop_attendees;
  const AttendeePresentationPrice =
    sponsorPerksData?.eventSponsorshipPerk?.attendees_price;
  const [editingKey, setEditingKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [isAddAttendee, setIsAddAttendee] = useState(false);
  const [disableAttendeeClicks, setDisableAttendeeClicks] = useState(false);
  const [disableEditClicks, setDisableEditClicks] = useState(false);
  const [isSavingAttendee, setIsSavingAttendee] = useState(false);

  const [attendeePresentationChecked, setAttendeePresentationChecked] =
    useState(false);
  const [attendeeDinnerChecked, setAttendeeDinnerChecked] = useState(false);
  const isEditing = (record) => record.id === editingKey;

  const handleAttendeePresentationChange = (e) => {
    setAttendeePresentationChecked(e.target.checked);
  };
  const handleAttendeeDinnerChange = (e) => {
    setAttendeeDinnerChecked(e.target.checked);
  };

  const isDraftRow = (row) =>
    !row?.id ||
    row.id === '' ||
    (row.id === '1' &&
      !row.attendee_first_name &&
      !row.attendee_last_name &&
      !row.attendee_email);

  const foodOptionValueForRecord = (
    record,
    foodOptions = allEventOptionsFoodData,
  ) => {
    const food = record?.event_registration_options?.food;
    if (!food) {
      return 'no-food';
    }
    const filterEventOptionData = foodOptions?.find(
      (obj) => obj.id === food?.event_option_id,
    );
    return food?.price === filterEventOptionData?.discounted_price
      ? filterEventOptionData?.value
      : food?.event_option_id;
  };

  const edit = (record) => {
    // A saved rep is not a new row. Cancel must not delete it.
    setIsAddAttendee(isDraftRow(record));
    setAttendeeDinnerChecked(record?.attending_dinner);
    setAttendeePresentationChecked(record?.attending_presentation);
    setEditingKey(record.id);
  };

  const foodOptionKey = (allEventOptionsFoodData || [])
    .map(
      (option) =>
        `${option?.value ?? option?.id}:${option?.discounted_price ?? ''}`,
    )
    .join('|');

  // Fill the form after the edit inputs mount. Setting values before that
  // leaves the row blank until the page is opened again.
  useEffect(() => {
    if (editingKey === '' || editingKey == null) {
      return;
    }
    const record = (attendeesDataWithPerks || []).find(
      (row) => row.id === editingKey,
    );
    if (!record) {
      return;
    }
    form.setFieldsValue({
      ...record,
      attendee_email_confirm: record?.attendee_email || '',
      food_option: foodOptionValueForRecord(record),
    });
  }, [editingKey]);

  useEffect(() => {
    if (editingKey === '' || editingKey == null) {
      return;
    }
    const record = (attendeesDataWithPerks || []).find(
      (row) => row.id === editingKey,
    );
    const foodOption = foodOptionValueForRecord(record);
    if (!record?.event_registration_options?.food || foodOption === 'no-food') {
      return;
    }
    form.setFieldsValue({food_option: foodOption});
  }, [editingKey, foodOptionKey]);

  const save = async (data) => {
    if (isSavingAttendee) return;

    try {
      setIsSavingAttendee(true);
      const emailTitle =
        ApplicationString['eventCommonTable.email']?.toLowerCase();
      const companyTitle =
        ApplicationString['eventCommonTable.company']?.toLowerCase();
      const requiredFieldErrors = getRequiredAttendeeFieldErrors(form, {
        requireRegistrationOption: false,
        requireFood: eventType?.toLowerCase() !== 'webcast' && !eventISOnline,
        messages: {
          firstName: ApplicationString['editableCell.pleaseFillOutFirstName'],
          lastName: ApplicationString['editableCell.pleaseFillOutLastName'],
          email: ApplicationString['editableCell.pleaseFillOut'](emailTitle),
          emailConfirm:
            ApplicationString['editableCell.pleaseConfirm'](emailTitle),
          foodOption:
            ApplicationString[
              'sponsorEditableCell.pleaseSelectAttendeeLunchOption'
            ] || ApplicationString['editableCell.pleaseSelectFoodOption'],
          company:
            ApplicationString['editableCell.pleaseFillOut'](companyTitle),
        },
      });
      if (requiredFieldErrors.length) {
        form.setFields(requiredFieldErrors);
        scrollAndFocusFormErrorField(requiredFieldErrors, form);
        setIsSavingAttendee(false);
        return;
      }
      const values = await form.validateFields();
      const registrationOptions = data?.event_registration_options;
      const checkIsSponsorOption =
        typeof values.food_option === 'string' &&
        values.food_option?.includes(uniqueSponsorPerksValueEnum);
      const splittedValue = checkIsSponsorOption
        ? Number(values?.food_option?.split('-')[0])
        : values.food_option;

      const sponsorData = {
        ...values,
        is_reps: true,
        event_option_perk_id: sponsorPerksData?.eventSponsorshipPerk?.id,
        event_id: Number(resolvedEventId),
        attending_presentation: attendeePresentationChecked,
        attending_dinner: attendeeDinnerChecked,
        registration_type:
          eventType?.toLowerCase() === 'webcast' || eventISOnline
            ? 'Webcast'
            : 'In-Person',
        food_option:
          values.food_option === 'no-food' ||
          eventType?.toLowerCase() === 'webcast' ||
          eventISOnline
            ? {}
            : {
                ...(checkIsSponsorOption && {
                  is_discounted: true,
                }),
                event_option_id: splittedValue,
              },
      };
      const editData = {
        ...values,
        event_option_perk_id: sponsorPerksData?.eventSponsorshipPerk?.id,
        is_reps: true,
        sponsorship_option_id:
          sponsorPerksData?.eventSponsorshipPerk?.event_option_id,
        event_id: Number(resolvedEventId),
        attending_presentation: attendeePresentationChecked,
        registration_type: 'In-Person',

        attending_dinner: attendeeDinnerChecked,
        food_option:
          values.food_option === 'no-food' ||
          eventType?.toLowerCase() === 'webcast' ||
          eventISOnline
            ? {}
            : {
                ...(checkIsSponsorOption && {
                  is_discounted: true,
                }),
                event_option_id: splittedValue,
                id: registrationOptions?.food?.id,
              },
      };
      if (isAddAttendee) {
        onSave(
          sponsorData,
          setEditingKey,
          form,
          setIsAddAttendee,
          setDisableAttendeeClicks,
          setDisablePaymentClicks,
          setDisableEditClicks,
          setIsSavingAttendee,
        );
      } else {
        onEdit(
          editData,
          editingKey,
          setEditingKey,
          form,
          setDisableAttendeeClicks,
          setDisablePaymentClicks,
          setDisableEditClicks,
          setIsSavingAttendee,
        );
      }
      setDisableAttendeeClicks(false);
    } catch (error) {
      setIsSavingAttendee(false);
      scrollAndFocusFormErrorField(error, form);
    }
  };

  const cancel = () => {
    if (isSavingAttendee) return;
    if (isAddAttendee) {
      const savedRows = (attendeesDataWithPerks || []).filter(
        (row) => !isDraftRow(row),
      );
      if (savedRows.length !== (attendeesDataWithPerks || []).length) {
        setListAttendeesDataWithPerks(savedRows);
      }
    }
    form.resetFields();
    setIsAddAttendee(false);
    setEditingKey('');
    setDisablePaymentClicks(false);
    setDisableAttendeeClicks(false);
    setDisableEditClicks(false);
  };

  useEffect(() => {
    if (!allowAddRow) {
      setIsAddAttendee(false);
      setLoading(false);
      return;
    }
    const hasNewUnsavedRow = attendeesDataWithPerks?.some(
      (attendee) =>
        !attendee.id ||
        attendee.id === '' ||
        (attendee.id === '1' &&
          !attendee.attendee_first_name &&
          !attendee.attendee_last_name &&
          !attendee.attendee_email),
    );

    const tableRow = {
      id: '1',
      attendee_first_name: '',
      attendee_last_name: '',
      attendee_company: '',
      attendee_email: '',
      event_registration_options: '',
      food_option: '',
      price: '',
      is_reps: '',
    };
    if (attendeesDataWithPerks && attendeesDataWithPerks?.length === 0) {
      setDisableAttendeeClicks(true);
      const loading =
        !attendeesDataWithPerks || attendeesDataWithPerks.length === 0;
      setLoading(loading);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
      setListAttendeesDataWithPerks((current) => {
        // Saved reps may already be in state from the same update flush.
        if (current?.length) return current;
        return [tableRow];
      });
    } else if (
      attendeesDataWithPerks?.length === 1 &&
      isDraftRow(attendeesDataWithPerks[0])
    ) {
      setDisableAttendeeClicks(true);
      if (editingKey !== attendeesDataWithPerks[0].id) {
        edit(attendeesDataWithPerks[0]);
      }
    } else if (
      attendeesDataWithPerks &&
      attendeesDataWithPerks?.length > 0 &&
      !hasNewUnsavedRow
    ) {
      setIsAddAttendee(false);
      setDisableAttendeeClicks(false);
      if (editingKey === '1') {
        setEditingKey('');
        form.resetFields();
      }
    }
  }, [attendeesDataWithPerks, allowAddRow]);

  const addRow = () => {
    const newRow = {
      id: '',
      attendee_first_name: '',
      attendee_last_name: '',
      attendee_company: '',
      attendee_email: '',
      event_registration_options: '',
      food_option: '',
      price: '',
      is_reps: '',
    };
    const existingAttendee = attendeesDataWithPerks.find((obj) =>
      Object.keys(newRow).every((key) => obj[key] === newRow[key]),
    );
    if (existingAttendee) {
      setDisableAttendeeClicks(true);
      return;
    } else {
      setIsAddAttendee(true);
      edit(newRow);
      setListAttendeesDataWithPerks([...attendeesDataWithPerks, newRow]);
      setDisableEditClicks(true);
    }
  };

  const getValue = (text) => {
    let value = null;
    let showIcon = false;
    if (text !== '' && text != null && Number(text) === 0) {
      value =
        ApplicationString['userAddAttendeeComponent.eventComanTable.free'];
      showIcon = true;
    }
    return {value, showIcon};
  };
  const columns = [
    {
      title: ApplicationString['eventCommonTable.name'],
      dataIndex: 'name',
      key: 'name',
      editable: true,
      width: '15%',
      render: (text, record) => {
        const fullName = `${record.attendee_first_name || 'Name'} ${
          record.attendee_last_name || ''
        }`;
        return <Tag prefixCls='regularAttendeesTag'>{fullName}</Tag>;
      },
    },
    {
      title: ApplicationString['eventCommonTable.email'],
      dataIndex: 'attendee_email',
      key: 'email',
      width: '18%',
      editable: true,
      render: (text) => {
        return <Tag prefixCls='regularAttendeesTag'>{text || 'Email'}</Tag>;
      },
    },

    {
      title: ApplicationString['eventCommonTable.lunch'],
      dataIndex: 'option',
      key: 'registration_and_food_option',
      hidden:
        eventType?.toLowerCase() === 'webcast' || eventISOnline ? true : false,
      editable: true,
      width: '20%',
      render: (_, record) => {
        const {event_registration_options: options} = record || {};
        const food = options?.food || {};
        const foodName =
          food?.name ||
          ApplicationString['addAttendeesComponent.label.noFoodOption'];
        const foodPrice = food?.price;

        return (
          <div className='option-tag-container'>
            {
              <Tag prefixCls='regularAttendeesTag'>
                {ApplicationString['addAttendeeComponent.option.food']}:{' '}
                {foodName}
                {foodPrice || foodPrice === 0
                  ? ` (${formatCurrencyByLocalChapter(
                      foodPrice,
                      eventLocalChapterData,
                    )})`
                  : ''}
              </Tag>
            }
          </div>
        );
      },
    },
    {
      title: meetingPresentationLabel(
        AttendeePresentationPrice,
        ApplicationString,
      ),
      dataIndex: 'attending_presentation',
      key: 'attending_presentation',
      width: '12%',
      hidden: AttendeePresentationLimit ? false : true,
      editable: true,
      render: (text) => {
        return (
          <div className='checkbox-editableCell'>{text ? 'Yes' : 'No'}</div>
        );
      },
    },
    {
      title: attendingDinnerLabel(AttendeeDinnerPrice, ApplicationString),
      dataIndex: 'attending_dinner',
      key: 'attending_dinner',
      width: '9%',
      editable: true,
      hidden: AttendeeDinnerLimit ? false : true,
      render: (text) => {
        return (
          <div className='checkbox-editableCell'>{text ? 'Yes' : 'No'}</div>
        );
      },
    },
    {
      title: ApplicationString['eventCommonTable.company'],
      dataIndex: 'attendee_company',
      key: 'company',
      width: '15%',
      editable: true,
      render: (text) => {
        return (
          <Tag prefixCls='regularAttendeesTag'>{text || 'Company Name'}</Tag>
        );
      },
    },
    {
      title: ApplicationString['eventCommonTable.price'],
      dataIndex: 'total_cost',
      key: 'price',
      width: '5%',
      render: (text) => {
        return (
          <h4 className='regularAttendees-price'>
            {formatCurrencyByLocalChapter(text, eventLocalChapterData)}
          </h4>
        );
      },
    },
    {
      title: ' ',
      dataIndex: 'is_reps',
      key: 'is_reps',
      width: '6%',
      render: (_, record) => {
        const {value, showIcon} = getValue(record?.total_cost);
        return (
          value && (
            <div className='custom-tag'>
              {showIcon && <DollarOutlined className='custom-tag-text' />}
              {value}
            </div>
          )
        );
      },
    },
    {
      title: ' ',
      dataIndex: 'action',
      width: '6%',
      fixed: 'right',
      render: (_, record) => {
        return (
          <AdminActionsDropdown
            record={record}
            usedForAttendeeTable={true}
            editBool={true}
            attendeeEditingKey={editingKey}
            handleCancelClick={cancel}
            handleSaveClick={save}
            deleteBool={true}
            handleEditClick={edit}
            handleDeleteClick={() => onDelete(record.id)}
            setDisableAttendeeClicks={setDisableAttendeeClicks}
            disableEditClicks={disableEditClicks}
            disableSaveClicks={isSavingAttendee}
            setDisableEditClicks={setDisableEditClicks}
            setDisablePaymentClicks={setDisablePaymentClicks}
            text={ApplicationString[`userEventSponsorComponent.deleteText`]}
          />
        );
      },
    },
  ];

  const vendorRepLimit =
    sponsorPerksData?.eventSponsorshipPerk?.vendor_rep_limit;
  const freeLunchLimit =
    sponsorPerksData?.eventSponsorshipPerk?.free_lunch;
  const lunchPerkPrice = Number(
    sponsorPerksData?.eventSponsorshipPerk?.lunch_price || 0,
  );
  // Capacity counts should include already-paid and unpaid cart reps (Add Vendor Reps),
  // but not refunded / refund-pending rows — same as API occupiesRepSlot.
  const capacityAttendees = (
    limitAttendees ||
    attendeesDataWithPerks ||
    []
  ).filter((attendee) => occupiesRepSlot(attendee?.status));

  // Match API: food priced at sponsor lunch_price, or bought below its list price,
  // is the free-lunch perk (not a free vendor rep).
  const isDiscountedLunchAttendee = (attendee) => {
    if (!(freeLunchLimit > 0)) return false;
    const food = attendee?.event_registration_options?.food;
    if (!food?.event_option_id) return false;
    if (food.is_discounted === true) return true;
    if (food.is_discounted === false) return false;
    if (
      food.list_price !== undefined &&
      food.list_price !== null &&
      Number(food.price) < Number(food.list_price)
    ) {
      return true;
    }
    return Number(food.price) === lunchPerkPrice;
  };

  const totalAddedAttendeesWithPresentationPerks =
    capacityAttendees?.filter((attendee) => attendee.attending_presentation) ||
    [];

  const totalAddedAttendeesWithDinnerPerks =
    capacityAttendees?.filter((attendee) => attendee.attending_dinner) || [];

  // Free vendor reps = no presentation/dinner + not sponsor free-lunch perk.
  // Includes no-food reps and normal (non-perk) food options.
  const attendeesWithoutPresentationAndDinner =
    capacityAttendees?.filter(
      (attendee) =>
        attendee.id !== '1' &&
        !attendee.attending_presentation &&
        !attendee.attending_dinner &&
        !isDiscountedLunchAttendee(attendee),
    ) || [];

  const isAttendeePresentationRemaining =
    AttendeePresentationLimit > 0
      ? totalAddedAttendeesWithPresentationPerks.length <
        AttendeePresentationLimit
      : false;

  const isAttendeeDinnerRemaining =
    AttendeeDinnerLimit > 0
      ? totalAddedAttendeesWithDinnerPerks.length < AttendeeDinnerLimit
      : false;

  const isAnyAttendeeRemaining =
    isAttendeePresentationRemaining || isAttendeeDinnerRemaining;

  let showVendorRepLimitAlert = false;
  if (vendorRepLimit === 0) {
    showVendorRepLimitAlert = false;
  } else {
    if (isAnyAttendeeRemaining) {
      showVendorRepLimitAlert = false;
    }
    showVendorRepLimitAlert =
      !isAnyAttendeeRemaining &&
      attendeesWithoutPresentationAndDinner.length > vendorRepLimit;
  }

  const filterColumns = (columns) => {
    return columns.filter((column) => {
      return !column.hidden;
    });
  };

  const columnsWithEdit = filterFoodOptionColumnsForSponsorAttendee(
    filterColumns(columns),
    eventType,
    isEditing,
    eventISOnline,
  );

  const renderBenefits = (benefits) => {
    return (
      <div
        className='perks-inter'
        dangerouslySetInnerHTML={{__html: benefits}}
      />
    );
  };

  return (
    <>
      {sponsorOption?.event_id || sponsorPerksData ? (
        <Row
          gutter={[0, flush ? 16 : 30]}
          className={flush ? '' : 'pbssMainContainerRow'}>
          <Col span={24}>
            {flush ? (
              <Form
                form={form}
                onValuesChange={(changedValues) =>
                  formValueChangeHandler(form, changedValues)
                }>
                <Table
                  components={{
                    body: {
                      cell: (props) => (
                        <SponsorEditableCell
                          index={props.index}
                          allEventOptionsFoodData={allEventOptionsFoodData}
                          eventType={eventType}
                          attendeesData={
                            limitAttendees || attendeesDataWithPerks
                          }
                          handleAttendeePresentationChange={
                            handleAttendeePresentationChange
                          }
                          checkedAttendeePresentation={
                            attendeePresentationChecked
                          }
                          handleAttendeeDinnerChange={
                            handleAttendeeDinnerChange
                          }
                          checkedAttendeeDinner={attendeeDinnerChecked}
                          SponsorPerks={sponsorPerksData?.eventSponsorshipPerk}
                          inPersonRemaining={inPersonRemaining}
                          sponsorOption={sponsorOption}
                          isAddAttendee={isAddAttendee}
                          {...props}
                        />
                      ),
                    },
                  }}
                  prefixCls='attendees-table-container'
                  scroll={{x: columns?.length * 150}}
                  dataSource={attendeesDataWithPerks}
                  pagination={false}
                  columns={columnsWithEdit}
                />
              </Form>
            ) : (
              <SectionLayout>
                <Form
                  form={form}
                  onValuesChange={(changedValues) =>
                    formValueChangeHandler(form, changedValues)
                  }>
                  <Skeleton
                    loading={loading}
                    prefixCls='table-skeleton'
                    active
                    paragraph={{rows: 1}}>
                    <div className='attendees-content-container'>
                      <div className='attendees-label'>
                        {
                          ApplicationString[
                            'userAddAttendeeComponent.sponsorDetail.perksLabel'
                          ]
                        }
                      </div>
                      <div className='attendees-description-container'>
                        {alertInformation && alertInformation !== '<p></p>' && (
                          <div className='attendees-description-container'>
                            <Alert
                              prefixCls='attendees-description-alert attendees-description-alertmsg'
                              message={renderBenefits(alertInformation)}
                              type='info'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <Table
                      components={{
                        body: {
                          cell: (props) => (
                            <SponsorEditableCell
                              index={props.index}
                              allEventOptionsFoodData={allEventOptionsFoodData}
                              eventType={eventType}
                              attendeesData={
                                limitAttendees || attendeesDataWithPerks
                              }
                              handleAttendeePresentationChange={
                                handleAttendeePresentationChange
                              }
                              checkedAttendeePresentation={
                                attendeePresentationChecked
                              }
                              handleAttendeeDinnerChange={
                                handleAttendeeDinnerChange
                              }
                              checkedAttendeeDinner={attendeeDinnerChecked}
                              SponsorPerks={
                                sponsorPerksData?.eventSponsorshipPerk
                              }
                              inPersonRemaining={inPersonRemaining}
                              sponsorOption={sponsorOption}
                              isAddAttendee={isAddAttendee}
                              {...props}
                            />
                          ),
                        },
                      }}
                      prefixCls='attendees-table-container'
                      scroll={{x: columns?.length * 150}}
                      dataSource={attendeesDataWithPerks}
                      pagination={false}
                      columns={columnsWithEdit}
                    />
                  </Skeleton>
                </Form>
              </SectionLayout>
            )}
          </Col>
          {allowAddRow && (
            <Col span={24}>
              {flush ? (
                <AddAttendeeeComponent
                  addRow={addRow}
                  alignRight
                  disable={disableAttendeeClicks}
                  setDisablePaymentClicks={setDisablePaymentClicks}
                  showVendorRepLimitAlert={showVendorRepLimitAlert}
                />
              ) : (
                <SectionLayout extendClass='addAttendeeWrapperEventSponsorComponent'>
                  <AddAttendeeeComponent
                    addRow={addRow}
                    disable={disableAttendeeClicks}
                    setDisablePaymentClicks={setDisablePaymentClicks}
                    showVendorRepLimitAlert={showVendorRepLimitAlert}
                  />
                </SectionLayout>
              )}
            </Col>
          )}
        </Row>
      ) : (
        ''
      )}
    </>
  );
};

export default CommonTable;
